import { getDatabase } from '../index';
import { calculateDiff, generateLocalId } from '../sync/diffUtils';
import * as PendingRepository from './PendingRepository';

/**
 * JurorRepository
 * 
 * CRUD operations for jurors with automatic diff tracking.
 * Includes seat management and efficient querying by seat number.
 */

/**
 * Default juror values
 */
const DEFAULT_JUROR = {
  nameAndId: '',
  seatNumber: null,
  alternateSeatNumber: null,
  comments: '',
  commentsStyles: null,
  demographics: [],
  discarded: false,
  alternateDiscarded: false,
  discardedReason: null,
  discarder: null,
  color: '#FFFFFF',
  revision: 0
};

/**
 * Get all jurors for a case
 * @param {string} caseId - Case ID
 * @returns {Promise<Array>}
 */
export async function getForCase(caseId) {
  const db = getDatabase();
  if (!db) return [];

  return db.jurors.where('caseId').equals(caseId).toArray();
}

/**
 * Get a juror by ID
 * @param {string} jurorId - Juror ID
 * @returns {Promise<Object|null>}
 */
export async function getById(jurorId) {
  const db = getDatabase();
  if (!db) return null;

  return db.jurors.get(jurorId);
}

/**
 * Get juror by seat number in a case
 * @param {string} caseId - Case ID
 * @param {number} seatNumber - Seat number
 * @returns {Promise<Object|null>}
 */
export async function getBySeat(caseId, seatNumber) {
  const db = getDatabase();
  // null/undefined is not a valid IndexedDB key component
  if (!db || seatNumber == null) return null;

  return db.jurors
    .where('[caseId+seatNumber]')
    .equals([caseId, seatNumber])
    .first();
}

/**
 * Get juror by alternate seat number in a case
 * @param {string} caseId - Case ID
 * @param {number} alternateSeatNumber - Alternate seat number
 * @returns {Promise<Object|null>}
 */
export async function getByAlternateSeat(caseId, alternateSeatNumber) {
  const db = getDatabase();
  // null/undefined is not a valid IndexedDB key component
  if (!db || alternateSeatNumber == null) return null;

  return db.jurors
    .where('[caseId+alternateSeatNumber]')
    .equals([caseId, alternateSeatNumber])
    .first();
}

/**
 * Get juror occupying a seat in the jury box or alternates panel.
 * @param {string} caseId - Case ID
 * @param {number} seatNumber - Seat number
 * @param {'jury-box'|'alternates'} [mode='jury-box']
 * @returns {Promise<Object|null>}
 */
export async function getJurorBySeat(caseId, seatNumber, mode = 'jury-box') {
  return mode === 'jury-box'
    ? getBySeat(caseId, seatNumber)
    : getByAlternateSeat(caseId, seatNumber);
}

/**
 * Get all seated jurors for a case (in jury box)
 * @param {string} caseId - Case ID
 * @returns {Promise<Array>}
 */
export async function getSeatedJurors(caseId) {
  const db = getDatabase();
  if (!db) return [];

  const jurors = await db.jurors.where('caseId').equals(caseId).toArray();
  return jurors.filter(j => 
    j.seatNumber != null && 
    j.seatNumber > 0 && 
    !j.discarded
  );
}

/**
 * Get all jurors in the pool (not seated, not discarded)
 * @param {string} caseId - Case ID
 * @returns {Promise<Array>}
 */
export async function getPoolJurors(caseId) {
  const db = getDatabase();
  if (!db) return [];

  const jurors = await db.jurors.where('caseId').equals(caseId).toArray();
  return jurors.filter(j => 
    (j.seatNumber == null || j.seatNumber <= 0) &&
    (j.alternateSeatNumber == null || j.alternateSeatNumber <= 0) &&
    !j.discarded &&
    !j.alternateDiscarded
  );
}

/**
 * Get discarded jurors for a case
 * @param {string} caseId - Case ID
 * @returns {Promise<Array>}
 */
export async function getDiscardedJurors(caseId) {
  const db = getDatabase();
  if (!db) return [];

  const jurors = await db.jurors.where('caseId').equals(caseId).toArray();
  return jurors.filter(j => j.discarded || j.alternateDiscarded);
}

/**
 * Create a new juror (locally)
 * @param {string} caseId - Parent case ID
 * @param {Object} jurorData - Juror data
 * @returns {Promise<Object>} - Created juror with local ID
 */
export async function create(caseId, jurorData) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const localId = generateLocalId();
  const now = new Date().toISOString();

  const newJuror = {
    ...DEFAULT_JUROR,
    ...jurorData,
    _id: localId,
    caseId,
    created: now,
    updated: now,
    revision: 0,
    _localOnly: true,
    _localId: localId
  };

  // If assigning to a seat, unseat any existing juror
  if (newJuror.seatNumber != null && newJuror.seatNumber > 0) {
    await unseatJurorAt(caseId, newJuror.seatNumber);
  }
  if (newJuror.alternateSeatNumber != null && newJuror.alternateSeatNumber > 0) {
    await unseatAlternateJurorAt(caseId, newJuror.alternateSeatNumber);
  }

  // Save to local database
  await db.jurors.add(newJuror);

  // Queue for sync
  await PendingRepository.addCreate(
    PendingRepository.EntityType.JUROR,
    localId,
    newJuror,
    caseId
  );

  console.log(`Juror created locally: ${localId}`);
  return newJuror;
}

/**
 * Create multiple jurors at once
 * @param {string} caseId - Parent case ID
 * @param {Array} jurorsData - Array of juror data
 * @returns {Promise<Array>} - Created jurors
 */
export async function createMany(caseId, jurorsData) {
  const created = [];
  for (const jurorData of jurorsData) {
    const juror = await create(caseId, jurorData);
    created.push(juror);
  }
  return created;
}

/**
 * Update a juror (locally)
 * Only changed fields are tracked for sync
 * @param {string} jurorId - Juror ID
 * @param {Object} updates - Fields to update
 * @param {Object} options - Options to apply during the operation
 * @returns {Promise<Object|null>} - Updated juror
 */
export async function update(jurorId, updates, options = {}) {
  // unseatConflicts: seat moves evict whoever already holds the target seat.
  // Seat RENUMBERING must skip this — the chair keeps its occupant and only
  // the label changes, so a juror elsewhere holding the colliding number
  // must not be kicked to the pool.
  const { unseatConflicts = true } = options;
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  // Get current juror — fall back to _localId lookup in case the record was
  // remapped from a local UUID to a server ID while the caller still holds
  // the old local ID (e.g. JurorDialog typing notes after create sync).
  let existing = await db.jurors.get(jurorId);
  if (!existing) {
    const remapped = await db.jurors.filter(j => j._localId === jurorId).first();
    if (remapped) {
      console.log(`Juror ID remapped ${jurorId} → ${remapped._id}, using server ID`);
      jurorId = remapped._id;
      existing = remapped;
    } else {
      console.warn(`Juror not found for update: ${jurorId}`);
      return null;
    }
  }

  const now = new Date().toISOString();

  // Calculate what actually changed (diff against the full merged state, not partial updates,
  // to avoid treating absent keys as deletions)
  const diff = calculateDiff(existing, { ...existing, ...updates });
  
  if (!diff) {
    console.log(`No changes detected for juror: ${jurorId}`);
    return existing;
  }

  // Handle seat changes - unseat any existing juror
  if (unseatConflicts && 'seatNumber' in updates && updates.seatNumber != null && updates.seatNumber > 0) {
    if (updates.seatNumber !== existing.seatNumber) {
      await unseatJurorAt(existing.caseId, updates.seatNumber, jurorId);
    }
  }
  if (unseatConflicts && 'alternateSeatNumber' in updates && updates.alternateSeatNumber != null && updates.alternateSeatNumber > 0) {
    if (updates.alternateSeatNumber !== existing.alternateSeatNumber) {
      await unseatAlternateJurorAt(existing.caseId, updates.alternateSeatNumber, jurorId);
    }
  }

  // Apply updates locally
  const updatedJuror = {
    ...existing,
    ...updates,
    updated: now
  };

  await db.jurors.put(updatedJuror);

  // Carry seat-conflict intent to the server. The server evicts whoever already
  // holds a target seat by default (so iPad moves work without sending the
  // displaced juror). A seat RENUMBER (unseatConflicts: false) must NOT trigger
  // that — custom seat numbers allow two spaces to share a number mid-relabel,
  // and a server-side eviction would bump the colliding juror to the pool a
  // second after the relabel syncs. A normal seat MOVE stamps the flag false so
  // it overrides any stale intent merged into a still-pending diff.
  const syncDiff = { ...diff, revision: existing.revision };
  const seatChanged =
    (diff.seatNumber != null && diff.seatNumber > 0) ||
    (diff.alternateSeatNumber != null && diff.alternateSeatNumber > 0);
  if (seatChanged) {
    syncDiff.allowSeatConflict = !unseatConflicts;
  }

  // Always queue for sync. PendingRepository merges updates into a pending
  // create when the juror is still local-only, so post-create edits (notes,
  // seat moves) are not lost.
  await PendingRepository.addUpdate(
    PendingRepository.EntityType.JUROR,
    jurorId,
    syncDiff,
    existing.revision,
    existing.caseId,
  );

  console.log(`Juror updated locally: ${jurorId}`, Object.keys(diff));
  return updatedJuror;
}

/**
 * Update multiple jurors in one Dexie transaction.
 * Used for batch jury box actions so live queries refresh once, not per juror.
 * @param {Array<{ jurorId: string, updates: Object }>} changes
 * @param {{ resolveSeatConflicts?: boolean }} [options]
 * @returns {Promise<Array<Object>>}
 */
export async function updateMany(changes, options = {}) {
  const { resolveSeatConflicts = false } = options;
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');
  if (!changes.length) return [];

  const mergedByJurorId = new Map();
  for (const { jurorId, updates } of changes) {
    const existingUpdates = mergedByJurorId.get(jurorId) || {};
    mergedByJurorId.set(jurorId, { ...existingUpdates, ...updates });
  }

  const normalizedChanges = [...mergedByJurorId.entries()].map(
    ([jurorId, updates]) => ({ jurorId, updates })
  );

  const now = new Date().toISOString();
  const updatedJurors = [];
  const pendingSync = [];

  await db.transaction('rw', db.jurors, async () => {
    for (const { jurorId, updates } of normalizedChanges) {
      const existing = await db.jurors.get(jurorId);
      if (!existing) {
        console.warn(`Juror not found for batch update: ${jurorId}`);
        continue;
      }

      if (resolveSeatConflicts) {
        if ('seatNumber' in updates && updates.seatNumber != null && updates.seatNumber > 0) {
          if (updates.seatNumber !== existing.seatNumber) {
            await unseatJurorAt(existing.caseId, updates.seatNumber, jurorId);
          }
        }
        if (
          'alternateSeatNumber' in updates &&
          updates.alternateSeatNumber != null &&
          updates.alternateSeatNumber > 0
        ) {
          if (updates.alternateSeatNumber !== existing.alternateSeatNumber) {
            await unseatAlternateJurorAt(
              existing.caseId,
              updates.alternateSeatNumber,
              jurorId
            );
          }
        }
      }

      const diff = calculateDiff(existing, { ...existing, ...updates });
      if (!diff) continue;

      const updatedJuror = {
        ...existing,
        ...updates,
        updated: now,
      };

      await db.jurors.put(updatedJuror);
      updatedJurors.push(updatedJuror);

      if (!existing._localOnly) {
        pendingSync.push({
          jurorId,
          diff,
          baseRevision: existing.revision,
          caseId: existing.caseId,
        });
      }
    }
  });

  for (const { jurorId, diff, baseRevision, caseId } of pendingSync) {
    await PendingRepository.addUpdate(
      PendingRepository.EntityType.JUROR,
      jurorId,
      { ...diff, revision: baseRevision },
      baseRevision,
      caseId
    );
  }

  console.log(`Batch updated ${updatedJurors.length} jurors`);
  return updatedJurors;
}

/**
 * Delete a juror (locally)
 * @param {string} jurorId - Juror ID
 * @returns {Promise<boolean>} - True if deleted
 */
export async function remove(jurorId) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const existing = await db.jurors.get(jurorId);
  if (!existing) {
    return false;
  }

  // Delete locally
  await db.jurors.delete(jurorId);

  // Queue for sync
  await PendingRepository.addDelete(
    PendingRepository.EntityType.JUROR,
    jurorId,
    existing.caseId
  );

  console.log(`Juror deleted locally: ${jurorId}`);
  return true;
}

/**
 * Upsert a juror (create or update without generating pending operations)
 * Used by SyncEngine when applying server data locally.
 * @param {Object} jurorData - Juror data (must include _id)
 */
export async function upsert(jurorData) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  await db.jurors.put({
    ...DEFAULT_JUROR,
    ...jurorData,
    _localOnly: false
  });
}

/**
 * Save juror from server (no pending operation created)
 * @param {Object} jurorData - Juror data from server
 */
export async function saveFromServer(jurorData) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const jurorToSave = {
    ...DEFAULT_JUROR,
    ...jurorData,
    _localOnly: false,
    _localId: null
  };

  await db.jurors.put(jurorToSave);
  console.log(`Juror saved from server: ${jurorData._id}`);
}

/**
 * Save multiple jurors from server (bulk operation)
 * @param {Array} jurors - Array of jurors from server
 */
export async function bulkSaveFromServer(jurors) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const jurorsToSave = jurors.map(j => ({
    ...DEFAULT_JUROR,
    ...j,
    _localOnly: false,
    _localId: null
  }));

  await db.jurors.bulkPut(jurorsToSave);
  console.log(`${jurors.length} jurors saved from server`);
}

/**
 * Update local juror ID after server assigns real ID
 * @param {string} localId - Local UUID
 * @param {string} serverId - Server-assigned ID
 * @param {Object} serverData - Full juror data from server
 */
export async function updateIdFromServer(localId, serverId, serverData) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  // Delete the local record
  await db.jurors.delete(localId);

  // Add the server record
  await saveFromServer(serverData);

  // Update pending operations
  await PendingRepository.updateEntityId(
    PendingRepository.EntityType.JUROR,
    localId,
    serverId
  );

  console.log(`Juror ID updated: ${localId} -> ${serverId}`);
}

/**
 * Apply a diff from server (WebSocket update)
 * @param {string} jurorId - Juror ID
 * @param {Object} diff - Changed fields from server
 */
export async function applyServerDiff(jurorId, diff) {
  const db = getDatabase();
  if (!db) return;

  const existing = await db.jurors.get(jurorId);
  if (!existing) {
    console.warn(`Juror not found for server diff: ${jurorId}`);
    return;
  }

  // Check revision to avoid overwriting newer local changes
  if (diff.revision && existing.revision > diff.revision) {
    console.log(`Ignoring older server diff for juror: ${jurorId}`);
    return;
  }

  const updated = {
    ...existing,
    ...diff,
    updated: new Date().toISOString()
  };

  await db.jurors.put(updated);
  console.log(`Server diff applied to juror: ${jurorId}`);
}

/**
 * Unseat a juror from a specific seat (move to pool)
 * @param {string} caseId - Case ID
 * @param {number} seatNumber - Seat number
 * @param {string} [excludeJurorId] - Juror ID to exclude (the one being seated)
 */
async function unseatJurorAt(caseId, seatNumber, excludeJurorId = null) {
  const db = getDatabase();
  if (!db) return;

  const currentJuror = await getBySeat(caseId, seatNumber);
  if (currentJuror && currentJuror._id !== excludeJurorId && !currentJuror.discarded) {
    await update(currentJuror._id, { seatNumber: null });
    console.log(`Unseated juror ${currentJuror._id} from seat ${seatNumber}`);
  }
}

/**
 * Unseat a juror from a specific alternate seat
 * @param {string} caseId - Case ID
 * @param {number} alternateSeatNumber - Alternate seat number
 * @param {string} [excludeJurorId] - Juror ID to exclude
 */
async function unseatAlternateJurorAt(caseId, alternateSeatNumber, excludeJurorId = null) {
  const db = getDatabase();
  if (!db) return;

  const currentJuror = await getByAlternateSeat(caseId, alternateSeatNumber);
  if (currentJuror && currentJuror._id !== excludeJurorId && !currentJuror.alternateDiscarded) {
    await update(currentJuror._id, { alternateSeatNumber: null });
    console.log(`Unseated alternate juror ${currentJuror._id} from seat ${alternateSeatNumber}`);
  }
}

/**
 * Move juror to a seat
 * @param {string} jurorId - Juror ID
 * @param {number} seatNumber - Seat number (null to move to pool)
 */
export async function seatJuror(jurorId, seatNumber) {
  return update(jurorId, { seatNumber });
}

/**
 * Dismiss a juror
 * @param {string} jurorId - Juror ID
 * @param {string} reason - 'peremptory' | 'cause' | 'hardship'
 * @param {string} discarder - 'prosecutor' | 'defense' | 'both'
 */
export async function dismissJuror(jurorId, reason, discarder) {
  return update(jurorId, {
    discarded: true,
    discardedReason: reason,
    discarder,
    seatNumber: null
  });
}

/**
 * Move a juror to a target seat in one atomic Dexie transaction.
 * Any juror already in that seat is displaced to the pool.
 * Wrapping both writes in a single transaction ensures subscribers receive
 * only one live-query notification instead of one per write.
 *
 * @param {string} caseId
 * @param {string} jurorId - The juror being moved
 * @param {number} targetSeatNumber
 * @param {'jury-box'|'alternates'} [mode='jury-box']
 */
export async function moveSeat(caseId, jurorId, targetSeatNumber, mode = 'jury-box') {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  const discardedField = mode === 'jury-box' ? 'discarded' : 'alternateDiscarded';

  // null/0 target means "move to the pool" — there is no occupant to
  // displace, and null is not a valid IndexedDB compound-index key, so the
  // seat lookup must be skipped entirely.
  const target = targetSeatNumber != null && targetSeatNumber > 0 ? targetSeatNumber : null;

  const [movedJuror, occupyingJuror] = await Promise.all([
    db.jurors.get(jurorId),
    target == null
      ? Promise.resolve(null)
      : mode === 'jury-box'
        ? getBySeat(caseId, target)
        : getByAlternateSeat(caseId, target),
  ]);

  if (!movedJuror) {
    console.warn(`moveSeat: juror ${jurorId} not found`);
    return [];
  }
  if ((movedJuror[seatField] ?? null) === target) return [];

  const now = new Date().toISOString();
  const pendingOps = [];

  await db.transaction('rw', db.jurors, async () => {
    await db.jurors.put({ ...movedJuror, [seatField]: target, updated: now });
    pendingOps.push({
      jurorId: movedJuror._id,
      diff: { [seatField]: target },
      before: { [seatField]: movedJuror[seatField] ?? null },
      revision: movedJuror.revision,
      caseId: movedJuror.caseId,
    });

    if (occupyingJuror && occupyingJuror._id !== jurorId && !occupyingJuror[discardedField]) {
      await db.jurors.put({ ...occupyingJuror, [seatField]: null, updated: now });
      pendingOps.push({
        jurorId: occupyingJuror._id,
        diff: { [seatField]: null },
        before: { [seatField]: target },
        revision: occupyingJuror.revision,
        caseId: occupyingJuror.caseId,
      });
    }
  });

  for (const op of pendingOps) {
    await PendingRepository.addUpdate(
      PendingRepository.EntityType.JUROR,
      op.jurorId,
      { ...op.diff, revision: op.revision },
      op.revision,
      op.caseId,
    );
  }

  // Change list for undo recording: what moved, from where, to where.
  return pendingOps.map(op => ({
    type: 'update',
    jurorId: op.jurorId,
    caseId: op.caseId,
    before: op.before,
    after: op.diff,
  }));
}

/**
 * Restore a dismissed juror to the pool
 * @param {string} jurorId - Juror ID
 */
export async function restoreJuror(jurorId) {
  return update(jurorId, {
    discarded: false,
    discardedReason: null,
    discarder: null
  });
}

/**
 * Get count of jurors for a case
 * @param {string} caseId - Case ID
 * @returns {Promise<number>}
 */
export async function getCount(caseId) {
  const db = getDatabase();
  if (!db) return 0;

  return db.jurors.where('caseId').equals(caseId).count();
}

/**
 * Delete all jurors for a case
 * @param {string} caseId - Case ID
 */
export async function removeAllForCase(caseId) {
  const db = getDatabase();
  if (!db) return;

  await db.jurors.where('caseId').equals(caseId).delete();
  console.log(`All jurors deleted for case: ${caseId}`);
}

/**
 * Clear all jurors (for testing/reset)
 */
export async function clearAll() {
  const db = getDatabase();
  if (!db) return;

  await db.jurors.clear();
  console.log('All jurors cleared');
}

export default {
  getForCase,
  getById,
  getBySeat,
  getByAlternateSeat,
  getJurorBySeat,
  getSeatedJurors,
  getPoolJurors,
  getDiscardedJurors,
  create,
  createMany,
  update,
  updateMany,
  remove,
  upsert,
  saveFromServer,
  bulkSaveFromServer,
  updateIdFromServer,
  applyServerDiff,
  seatJuror,
  dismissJuror,
  restoreJuror,
  getCount,
  removeAllForCase,
  clearAll
};
